import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { useZoneLabels } from "@/hooks/use-zone-labels";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, PawPrint, GripVertical, Heart } from "lucide-react";
import { useFollowedAnimalIds } from "@/hooks/use-followed-animals";
import AnimalDetailDrawer from "@/components/animals/AnimalDetailDrawer";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { TaskAnimalLink } from "./task-types";

interface AnimalSearchResult {
  animalId: string;
  name: string;
  species: string | null;
  primaryBreed: string | null;
  location: string | null;
  zoneCode: string | null;
}

interface TaskAnimalLinkerProps {
  taskId: string;
  animals: TaskAnimalLink[];
  canEdit: boolean;
  animalSearchQuery: string;
  setAnimalSearchQuery: (q: string) => void;
  animalSearchResults: AnimalSearchResult[] | undefined;
}

function AnimalItemContent({
  animal,
  canEdit,
  onOpenDrawer,
  onUnlink,
  dragHandle,
  isDragOverlay,
  followedIds,
  resolveLabel,
}: {
  animal: TaskAnimalLink;
  canEdit: boolean;
  onOpenDrawer: (animalId: string) => void;
  onUnlink: (id: string) => void;
  dragHandle?: React.ReactNode;
  isDragOverlay?: boolean;
  followedIds: string[];
  resolveLabel: (code: string) => string;
}) {
  return (
    <div
      className={`flex items-start justify-between gap-1 py-1 ${isDragOverlay ? "rounded-md border border-border bg-background shadow-md px-1" : ""}`}
      data-testid={`task-animal-link-${animal.id}`}
    >
      <div className="flex items-start gap-1 min-w-0">
        {dragHandle}
        <button
          className="flex items-start gap-2 text-left hover:underline min-w-0"
          onClick={() => onOpenDrawer(animal.animalId)}
          data-testid={`task-button-open-animal-${animal.id}`}
        >
          <AnimalAvatar
            animalId={animal.animalId}
            photoUrl={animal.animalPhotoUrl ?? null}
            name={animal.animalName ?? animal.animalId}
            species={animal.animalSpecies ?? ""}
            className="h-5 w-5 shrink-0"
          />
          <span className="text-sm break-words">
            {animal.animalName ?? animal.animalId}
            {followedIds.includes(animal.animalId) && <Heart className="h-3 w-3 fill-rose-500 text-rose-500 inline-block align-middle ml-0.5" data-testid={`heart-followed-linked-${animal.animalId}`} />}
            {animal.animalSpecies && <> <Badge variant="outline" className="text-xs" data-testid={`badge-species-${animal.id}`}>{animal.animalSpecies.toUpperCase()}</Badge></>}
            {animal.animalName && <span className="text-muted-foreground ml-1">({animal.animalId})</span>}
            {animal.zoneCode && <> <Badge variant="outline" className="text-xs">{resolveLabel(animal.zoneCode)}</Badge></>}
            {animal.locationCode && <> <Badge variant="outline" className="text-xs">{animal.locationCode}</Badge></>}
          </span>
        </button>
      </div>
      {canEdit && (
        <Button variant="ghost" size="icon" onClick={() => onUnlink(animal.id)} data-testid={`task-button-unlink-animal-${animal.id}`}>
          <X className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

function SortableAnimalItem({
  animal,
  activeId,
  onOpenDrawer,
  onUnlink,
  followedIds,
  resolveLabel,
}: {
  animal: TaskAnimalLink;
  activeId: string | null;
  onOpenDrawer: (animalId: string) => void;
  onUnlink: (id: string) => void;
  followedIds: string[];
  resolveLabel: (code: string) => string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: animal.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  if (isDragging) {
    return (
      <div ref={setNodeRef} style={style} className="rounded-md border-2 border-dashed border-muted-foreground/30 bg-muted/30 py-3 my-0.5" />
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <AnimalItemContent
        animal={animal}
        canEdit={true}
        onOpenDrawer={onOpenDrawer}
        onUnlink={onUnlink}
        followedIds={followedIds}
        resolveLabel={resolveLabel}
        dragHandle={
          <span {...attributes} {...listeners} className="cursor-grab text-muted-foreground shrink-0 mt-0.5">
            <GripVertical className="w-3 h-3" />
          </span>
        }
      />
    </div>
  );
}

export function TaskAnimalLinker({
  taskId,
  animals,
  canEdit,
  animalSearchQuery,
  setAnimalSearchQuery,
  animalSearchResults,
}: TaskAnimalLinkerProps) {
  const { toast } = useToast();
  const { resolveLabel } = useZoneLabels();
  const { data: followsData } = useFollowedAnimalIds();
  const followedIds = followsData?.animalIds ?? [];
  const [animalDrawerOpen, setAnimalDrawerOpen] = useState(false);
  const [selectedAnimalId, setSelectedAnimalId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const linkAnimalMutation = useMutation({
    mutationFn: async (animalId: string) => apiRequest("POST", `/api/tasks/${taskId}/animals`, { animalId }),
    onSuccess: () => { invalidateTaskQueries(); setAnimalSearchQuery(""); },
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const unlinkAnimalMutation = useMutation({
    mutationFn: async (linkId: string) => apiRequest("DELETE", `/api/tasks/${taskId}/animals/${linkId}`),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => apiRequest("POST", `/api/tasks/${taskId}/animals/reorder`, { orderedIds }),
    onError: (error: Error) => {
      invalidateTaskQueries();
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = animals.findIndex(a => a.id === active.id);
    const newIndex = animals.findIndex(a => a.id === over.id);
    const reordered = arrayMove(animals, oldIndex, newIndex);

    const cached = queryClient.getQueryData<{ task: any }>(["/api/tasks", taskId]);
    if (cached) {
      queryClient.setQueryData(["/api/tasks", taskId], {
        ...cached,
        task: { ...cached.task, animals: reordered },
      });
    }

    reorderMutation.mutate(reordered.map(a => a.id));
  }

  function handleOpenDrawer(animalId: string) {
    setSelectedAnimalId(animalId);
    setAnimalDrawerOpen(true);
  }

  const activeAnimal = activeId ? animals.find(a => a.id === activeId) : null;

  return (
    <>
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
          <PawPrint className="w-3 h-3" /> Animals
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1" data-testid="badge-animals-count">
            {animals.length}
          </Badge>
        </Label>
        {canEdit && (
          <div className="space-y-1">
            <Input
              placeholder="Search animals to add..."
              value={animalSearchQuery}
              onChange={e => setAnimalSearchQuery(e.target.value)}
              className="h-8 text-sm"
              data-testid="task-input-search-animal"
            />
            {animalSearchResults && animalSearchResults.length > 0 && (
              <div className="border rounded-md max-h-32 overflow-y-auto list-zebra shadow-sm">
                {animalSearchResults.map(a => (
                  <button
                    key={a.animalId}
                    className="w-full text-left px-2 py-1 text-sm hover-elevate"
                    onClick={() => linkAnimalMutation.mutate(a.animalId)}
                    data-testid={`task-button-link-animal-${a.animalId}`}
                  >
                    <span className="font-medium">{a.name}</span>
                    {followedIds.includes(a.animalId) && <Heart className="h-3 w-3 fill-rose-500 text-rose-500 inline-block align-middle ml-0.5" data-testid={`heart-followed-${a.animalId}`} />}
                    <span className="text-muted-foreground ml-1">({a.animalId})</span>
                    {a.species && <span className="text-muted-foreground ml-1">· {a.species}</span>}
                    {a.primaryBreed && <span className="text-muted-foreground ml-1">· {a.primaryBreed}</span>}
                    {a.location && <span className="text-muted-foreground ml-1">· {a.location}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="list-zebra">
          {canEdit && animals.length > 1 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <SortableContext items={animals.map(a => a.id)} strategy={verticalListSortingStrategy}>
                {animals.map(a => (
                  <SortableAnimalItem
                    key={a.id}
                    animal={a}
                    activeId={activeId}
                    onOpenDrawer={handleOpenDrawer}
                    onUnlink={(id) => unlinkAnimalMutation.mutate(id)}
                    followedIds={followedIds}
                    resolveLabel={resolveLabel}
                  />
                ))}
              </SortableContext>
              <DragOverlay>
                {activeAnimal && (
                  <AnimalItemContent
                    animal={activeAnimal}
                    canEdit={false}
                    onOpenDrawer={() => {}}
                    onUnlink={() => {}}
                    followedIds={followedIds}
                    resolveLabel={resolveLabel}
                    isDragOverlay
                    dragHandle={
                      <span className="cursor-grabbing text-muted-foreground shrink-0 mt-0.5">
                        <GripVertical className="w-3 h-3" />
                      </span>
                    }
                  />
                )}
              </DragOverlay>
            </DndContext>
          ) : (
            animals.map(a => (
              <AnimalItemContent
                key={a.id}
                animal={a}
                canEdit={canEdit}
                onOpenDrawer={handleOpenDrawer}
                onUnlink={(id) => unlinkAnimalMutation.mutate(id)}
                followedIds={followedIds}
                resolveLabel={resolveLabel}
              />
            ))
          )}
        </div>
      </div>

      {selectedAnimalId && (
        <AnimalDetailDrawer
          open={animalDrawerOpen}
          onOpenChange={(open) => {
            setAnimalDrawerOpen(open);
            if (!open) setSelectedAnimalId(null);
          }}
          animalId={selectedAnimalId}
        />
      )}
    </>
  );
}
